import { initializeApp } from 'firebase/app'
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore/lite'

// PENDIENTE: el proyecto Firebase todavía no existe. Estos valores se leen de
// .env.local (ver .env.example) y estarán vacíos hasta que se cree el proyecto.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

export const REGION = 'us-central1'
export const usandoEmuladores = import.meta.env.VITE_USE_EMULATORS === 'true'

export const app = initializeApp(firebaseConfig)

// Se usa el SDK "lite" de Firestore: pesa una fracción del completo porque no
// trae el motor de tiempo real ni la caché offline. El catálogo son 5-15
// productos que cambian rara vez, así que una lectura puntual basta y el
// ahorro va directo al presupuesto de carga.
export const db = getFirestore(app)

// A diferencia de Functions, Auth no se carga bajo demanda: el encabezado
// necesita saber si hay sesión iniciada desde el primer render (para mostrar
// "Iniciar sesión" o el avatar), así que no hay un punto natural donde
// diferir la carga sin un parpadeo de contenido.
// initializeAuth(...) en vez de getAuth(app): getAuth() adjunta por defecto
// browserPopupRedirectResolver, y ese resolver se auto-inicializa de forma
// "proactiva" en navegadores móviles/Safari (ver _shouldInitProactively en
// el SDK) — eso es justo lo que carga el iframe de 93 KB
// (firebaseapp.com/__/auth/iframe.js) en cada visita, la use o no. Sin
// resolver aquí, onAuthStateChanged sigue restaurando la sesión guardada
// de inmediato (usa persistencia, no el iframe); el resolver se importa y
// se pasa explícito solo al llamar signInWithPopup, en SesionProvider.jsx.
export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
})

if (usandoEmuladores) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
}

let promesaFunctions = null

/**
 * Carga el SDK de Cloud Functions bajo demanda. Solo hace falta cuando alguien
 * pulsa "Pedir por WhatsApp", así que no tiene por qué estar en el bundle
 * inicial. El módulo queda cacheado tras la primera llamada.
 */
export function cargarFunctions() {
  promesaFunctions ??= import('firebase/functions').then(
    ({ connectFunctionsEmulator, getFunctions, httpsCallable }) => {
      const functions = getFunctions(app, REGION)
      if (usandoEmuladores) {
        connectFunctionsEmulator(functions, '127.0.0.1', 5001)
      }
      return { functions, httpsCallable }
    }
  )

  return promesaFunctions
}

let promesaStorage = null

/**
 * Carga el SDK de Firebase Storage bajo demanda: solo hace falta en el panel
 * admin (subir/borrar fotos de producto), nunca en el catálogo público.
 */
export function cargarStorage() {
  promesaStorage ??= import('firebase/storage').then(
    ({ connectStorageEmulator, deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable }) => {
      const storage = getStorage(app)
      if (usandoEmuladores) {
        connectStorageEmulator(storage, '127.0.0.1', 9199)
      }
      return { storage, ref, uploadBytesResumable, getDownloadURL, deleteObject }
    }
  )

  return promesaStorage
}
